import { useCallback, useRef, useSyncExternalStore } from "react";
import { track } from "@/lib/track";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";

// Thin client for the renter AI concierge: owns the anonymous-visitor id and the
// per-tenant session id (both in localStorage for continuity across reloads),
// reads the public on/off + greeting via a SECURITY DEFINER RPC (ai_tone_config
// itself is admin-only), and wraps the edge-function call.

/** Which surface is talking: stamped on analytics so the two can be told apart. */
export type ConciergeSurface = "home" | "inbox" | "deck";

export interface ConciergeCard {
  slug: string;
  title: string;
  price: number;
  priceMax: number | null;
  beds: number;
  baths: number;
  area: string;
  image: string | null;
  images?: string[];
  match: number;
}

/** Accumulated renter preferences from the chat (mirrors the edge fn's ScorePrefs). */
export interface ConciergePrefs {
  budgetMin?: number | null;
  budgetMax?: number | null;
  preferredAreas?: string[] | null;
  preferredTypes?: string[] | null;
  preferredBedrooms?: number | null;
  preferredBathrooms?: number | null;
  preferredAmenities?: string[] | null;
  preferredServices?: string[] | null;
  moveInMonths?: string[] | null;
  numPeople?: number | null;
  hasPets?: boolean | null;
}

export interface ConciergeResponse {
  sessionId?: string;
  reply: string | null;
  cards?: ConciergeCard[];
  action?: string | null;
  handoff?: boolean;
  prefs?: ConciergePrefs;
  error?: string;
  /** A request the concierge proposed; nothing is sent until the renter taps Send. */
  pendingContact?: PendingContact;
}

export interface PendingContact {
  slug: string;
  title: string;
  message: string;
}

export interface TailMsg {
  role: "user" | "assistant" | "agent";
  text: string;
  cards?: ConciergeCard[];
  at?: string;
}

export interface SessionTail {
  handoff: boolean;
  display: TailMsg[];
}

const ANON_KEY = "msth_concierge_anon";
const SESSION_KEY_PREFIX = "msth_concierge_session_";

const getAnonId = (): string => {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
};

// --- session id store ---------------------------------------------------------
// One localStorage-backed value per tenant, shared by every hook instance on
// the page (the inbox's pinned row and its thread both need it) and across
// tabs through the `storage` event. Plain module state + useSyncExternalStore
// keeps every instance in step the moment a first message creates a session.

const sessionListeners = new Set<() => void>();

const readSessionId = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSessionId = (key: string, id: string | null) => {
  try {
    if (id) localStorage.setItem(key, id);
    else localStorage.removeItem(key);
  } catch {
    /* storage unavailable, instances still update through the listeners */
  }
  sessionListeners.forEach((l) => l());
};

const subscribeSessionId = (listener: () => void) => {
  sessionListeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (!e.key || e.key.startsWith(SESSION_KEY_PREFIX)) listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    sessionListeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
};

export const conciergeSessionKey = (tenantId: string) => `${SESSION_KEY_PREFIX}${tenantId}`;

export function useAiConcierge(opts: { surface?: ConciergeSurface } = {}) {
  const tenant = useTenant();
  const surface = opts.surface;
  const anonIdRef = useRef<string>(getAnonId());
  const sessionKey = conciergeSessionKey(tenant.id);
  const sessionId = useSyncExternalStore(
    subscribeSessionId,
    () => readSessionId(sessionKey),
    () => null,
  );

  const config = useQuery({
    queryKey: ["concierge-config", tenant.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_concierge_public_config", {
        _tenant_id: tenant.id,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : null;
      return { enabled: !!row?.enabled, greeting: row?.greeting ?? "", name: (row as { concierge_name?: string } | null)?.concierge_name || "Mar" };
    },
    staleTime: 5 * 60 * 1000,
  });

  const send = useCallback(
    async (message: string): Promise<ConciergeResponse> => {
      const { data, error } = await supabase.functions.invoke("renter-chat-concierge", {
        body: {
          tenantId: tenant.id,
          sessionId: sessionId ?? undefined,
          anonId: anonIdRef.current,
          message,
        },
      });
      if (error) {
        // Pull a structured error string out of the function response if present.
        let detail = error.message;
        const ctx = (error as { context?: Response }).context;
        if (ctx && typeof ctx.json === "function") {
          try {
            const b = await ctx.json();
            if (b?.error) detail = String(b.error);
          } catch {
            /* body wasn't JSON */
          }
        }
        return { reply: null, error: detail };
      }
      const res = (data ?? {}) as ConciergeResponse;
      track("concierge_message", surface ? { first: !sessionId, surface } : { first: !sessionId });
      if (res.handoff) track("concierge_handoff", surface ? { surface } : {});
      if (res.sessionId && res.sessionId !== sessionId) writeSessionId(sessionKey, res.sessionId);
      return res;
    },
    [tenant.id, sessionId, sessionKey, surface],
  );

  // The renter's own tap on a proposed request: the only way one is sent.
  const confirmContact = useCallback(
    async (pending: PendingContact): Promise<ConciergeResponse> => {
      const { data, error } = await supabase.functions.invoke("renter-chat-concierge", {
        body: {
          tenantId: tenant.id,
          sessionId: sessionId ?? undefined,
          anonId: anonIdRef.current,
          confirmContact: { slug: pending.slug, message: pending.message },
        },
      });
      if (error) return { reply: null, error: error.message };
      return (data ?? {}) as ConciergeResponse;
    },
    [tenant.id, sessionId],
  );

  // Owner-scoped read of the session transcript: the renter can't read
  // ai_chat_sessions directly (admin-only RLS). Used to restore history and to
  // receive the admin's ("team") replies during a human takeover.
  const fetchTail = useCallback(async (): Promise<SessionTail | null> => {
    if (!sessionId) return null;
    const { data, error } = await supabase.rpc("get_concierge_session_messages", {
      _session_id: sessionId,
      _anon_id: anonIdRef.current,
    });
    if (error) return null;
    const row = Array.isArray(data) ? data[0] : null;
    if (!row) return null;
    return {
      handoff: !!row.handoff,
      display: Array.isArray(row.display) ? (row.display as unknown as TailMsg[]) : [],
    };
  }, [sessionId]);

  const resetSession = useCallback(() => {
    writeSessionId(sessionKey, null);
  }, [sessionKey]);

  return {
    enabled: config.data?.enabled ?? false,
    greeting: config.data?.greeting ?? "",
    /** The concierge's name for this market (Mar by default). */
    name: config.data?.name ?? "Mar",
    configLoading: config.isLoading,
    sessionId,
    send,
    confirmContact,
    fetchTail,
    resetSession,
  };
}
